import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useIsNarrow } from '../../hooks/useIsNarrow';

// Share controls for the artist / track / mix headers.
//
//  - Desktop (wide): two inline controls — a quiet "copy link" ghost icon
//    and the bold "SHARE" promo pill (the branded stats-image export that
//    drives Instagram sharing). One-click each, no extra step.
//  - Mobile / mobile-web (<=768px): ONE "Share" button that opens a small
//    menu with "Share image" + "Share page" (the latter hands the URL to the
//    OS share sheet) — the native one-button pattern, so the cramped header
//    isn't crowded by three separate controls.
//
// Purely presentational: the parent owns the actions and passes them in.

const ShareIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12v7a2 2 0 002 2h12a2 2 0 002-2v-7" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" />
    </svg>
);
const LinkIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
);
const ImageIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" />
    </svg>
);
const ShareNodesIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
);

function ShareMenu({ onShareImage, onSharePage, onCopyLink, sharing, shareRef, quiet = false }) {
    const isNarrow = useIsNarrow();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
    }, [open]);

    const pickImage = useCallback(() => { setOpen(false); onShareImage(); }, [onShareImage]);
    // "Share page" hands the canonical URL to the OS share sheet (native Capacitor Share).
    const pickShare = useCallback(() => { setOpen(false); onSharePage(); }, [onSharePage]);

    const toggleMenu = useCallback(() => setOpen((v) => !v), []);

    if (isNarrow) {
        return (
            <div className="share-menu-wrap" ref={wrapRef}>
                <button
                    ref={shareRef}
                    className="export-share-btn"
                    onClick={toggleMenu}
                    disabled={sharing}
                    title="Share"
                    aria-haspopup="menu"
                    aria-expanded={open}
                >
                    {sharing ? <span className="export-spinner" /> : <ShareIcon />}
                    <span>{sharing ? 'Sharing...' : 'Share'}</span>
                </button>
                {open && (
                    <div className="share-menu" role="menu">
                        <button role="menuitem" onClick={pickImage} disabled={sharing}>
                            <ImageIcon /><span>Share image</span>
                        </button>
                        <button role="menuitem" onClick={pickShare}>
                            <ShareNodesIcon /><span>Share page</span>
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Desktop: quiet link icon + bold SHARE pill.
    // In `quiet` mode the SHARE promo is demoted to a small ghost icon (used
    // on the artist page where Follow is the loud primary CTA instead).
    return (
        <>
            <button className="share-link-btn" onClick={onCopyLink} title="Copy link">
                <LinkIcon />
            </button>
            <button ref={shareRef} className={`export-share-btn${quiet ? ' quiet' : ''}`} onClick={onShareImage} disabled={sharing} title="Share image">
                {sharing ? <span className="export-spinner" /> : <ShareIcon />}
                {!quiet && <span>{sharing ? 'Sharing...' : 'Share'}</span>}
            </button>
        </>
    );
}

export default ShareMenu;
